import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import {settings} from '@/plugins/settings';

// Files attached to received emails. The helpdesk only stores their metadata; the files are
// read from Gmail when opened (and kept a few days in the server cache).

export interface IEmailAttachment {
  part_id: string;
  filename: string;
  mime: string;
  size: number;
  content_id: string | null;
  // Already shown inside the email body through `cid:` (signature logos...).
  embedded: boolean;
}

const PREVIEW_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp'];
// Bigger photos are offered as a download instead of being loaded as a thumbnail.
const MAX_THUMBNAIL_BYTES = 15 * 1024 * 1024;

const listCache: {[emailId: number]: Promise<IEmailAttachment[]>} = {};
const fileCache: {[key: string]: Promise<string>} = {};

export function isPreviewImage(attachment: IEmailAttachment): boolean {
  return PREVIEW_IMAGE_TYPES.indexOf(attachment.mime) !== -1 && attachment.size <= MAX_THUMBNAIL_BYTES;
}

/** Files the reader should see: everything but the images the body already shows. */
export function visibleAttachments(attachments: IEmailAttachment[] | null | undefined): IEmailAttachment[] {
  return (attachments || []).filter((a) => !a.embedded);
}

export function listAttachments(emailId: number): Promise<IEmailAttachment[]> {
  if (!listCache[emailId]) {
    listCache[emailId] = helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + emailId + '/attachments', method: 'GET'})
      .then((res: any) => (res.data && res.data.data) || [])
      .catch((error: any) => {
        delete listCache[emailId];
        throw error;
      });
  }
  return listCache[emailId];
}

/** A blob URL for the file. Only images and PDFs keep their type, the rest can't render in our origin. */
export function attachmentUrl(emailId: number, attachment: IEmailAttachment): Promise<string> {
  const key = emailId + ':' + attachment.part_id;
  if (!fileCache[key]) {
    fileCache[key] = helpdeskAxios({
      url: settings.helpdeskEndpoint + 'v1/inbox/' + emailId + '/attachments/' + attachment.part_id,
      method: 'GET',
      responseType: 'blob',
    })
      .then((res: any) => {
        const previewable = PREVIEW_IMAGE_TYPES.indexOf(attachment.mime) !== -1 || attachment.mime === 'application/pdf';
        return URL.createObjectURL(new Blob([res.data], {type: previewable ? attachment.mime : 'application/octet-stream'}));
      })
      .catch((error: any) => {
        delete fileCache[key];
        throw error;
      });
  }
  return fileCache[key];
}

export function saveAttachment(emailId: number, attachment: IEmailAttachment): Promise<void> {
  return attachmentUrl(emailId, attachment).then((url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',') + ' MB';
}

export function attachmentIcon(attachment: IEmailAttachment): {icon: string; color: string} {
  const m = attachment.mime;
  const name = attachment.filename.toLowerCase();
  if (m === 'application/pdf') return {icon: 'mdi-file-pdf-box', color: '#c9372c'};
  if (m.indexOf('image/') === 0) return {icon: 'mdi-file-image-outline', color: '#1d7afc'};
  if (/word|\.docx?$/.test(m + name)) return {icon: 'mdi-file-word-box', color: '#0c66e4'};
  if (/sheet|excel|csv|\.xlsx?$/.test(m + name)) return {icon: 'mdi-file-excel-box', color: '#216e4e'};
  if (/zip|compressed|rar|7z/.test(m + name)) return {icon: 'mdi-folder-zip-outline', color: '#7f5f01'};
  if (m.indexOf('video/') === 0) return {icon: 'mdi-file-video-outline', color: '#5e4db2'};
  if (m.indexOf('audio/') === 0) return {icon: 'mdi-file-music-outline', color: '#5e4db2'};
  return {icon: 'mdi-file-outline', color: '#626f86'};
}
